import { db } from '../../firebase/config.js';
import { v4 as uuid } from 'uuid'
import firebase from '../../firebase/compat'
import { RRule } from 'rrule';
import { client } from '../Client.js';
import describeQueryError from '../../helpers/firestore/describeQueryError';
import notDeleted from '../../helpers/notDeleted.js';

/** @typedef {import('../../types/Firebase').Task} Task */

/**
 * @callback GetTask
 * @param {string} docId - Firebase document ID.
 * @returns {Promise<Task|null>} The task object or null if not found.
 */

/**
 * @typedef {'this' | 'following' | 'all'} DeleteType
 * Delete strategy for a recurring task:
 * - 'this': Delete only this occurrence
 * - 'following': Delete this and all following occurrences
 * - 'all': Delete the entire recurring series
 */

/**
 * Creates a new task in Firestore.
 * @callback CreateTask
 * @param {Task} task - The task object to create.
 * @returns {Promise<void>}
 */

/**
 * Updates an existing task with partial fields.
 * @callback UpdateTask
 * @param {string} docId - Firebase document ID.
 * @param {Partial<Task>} updates - Fields to update.
 * @returns {Promise<void>}
 */

/**
 * Saves a task - creates if no docid exists, updates otherwise.
 * Handles rrule serialization as well.
 * 
 * @callback SaveTask
 * @param {Task} task - The task object to save.
 * @returns {Promise<void>}
 */

/**
 * Deletes a task from Firestore.
 * @callback RemoveTask
 * @param {string} docId - Firebase document ID.
 * @returns {Promise<void>}
 */

/**
 * Creates a new unsaved task object with default values.
 * @callback NewTaskDoc
 * @param {object} options - Configuration options.
 * @param {Date} [options.startDate] - Start date for the task.
 * @param {Date} [options.endDate] - End date for the task.
 * @param {string} options.userId - User ID who owns the task.
 * @param {string} options.companyId - Company ID associated with the 
 * @param {string} [options.AdressID] - Address ID associated with the task.
 * @param {string} [options.PersonaID] - Persona ID associated with the task.
 * @param {string} [options.MeasureMomentID] - Measure Moment ID associated with the task.
 * @param {string} [options.ActivityFormID] - Activity Form ID associated with the task.
 * @param {string} [options.TeamMemberID] - Team Member ID associated with the task.
 * @param {string} [options.Type] - Type associated with the
 * task.
 * @returns {Task} New task object.
 */

/**
 * Handles deletion of recurring events with different strategies.
 * @callback DeleteRecurringTaskItem
 * @param {object} options - Delete options.
 * @param {Task} options.task - The task to delete.
 * @param {DeleteType} options.deleteType - Delete strategy.
 * @param {Date} options.date - The date of the selected occurrence.
 * @returns {Promise<void>}
 */

/**
 * Retrieves tasks within a date range and subscribes to changes.
 * @callback QueryBy
 * @param {object} options
 * @param {function(firebase.firestore.Query): firebase.firestore.Query} options.query - Function to modify the base query.
 * @param {Date} options.dateStart - Start date for the query.
 * @param {Date} options.dateEnd - End date for the query.
 * @param {function(Task[]): void} options.onUpdate - Callback invoked when tasks update.
 * @param {boolean} [options.alwaysIncludeRecurring=true] - Whether to include recurring tasks.
 * @returns {function(): void} Firebase unsubscribe function.
 */

/**
 * @typedef {object} TaskDTO
 * @property {GetTask} get - Retrieves a task by document ID.
 * @property {NewTaskDoc} newDoc - Creates a new *unsaved* task object.
 * @property {CreateTask} create - Creates a new task.
 * @property {UpdateTask} update - Updates an existing task.
 * @property {SaveTask} save - Saves a task (create or update).
 * @property {RemoveTask} remove - Deletes a task.
 * @property {DeleteRecurringTaskItem} deleteRecurringItem - Deletes a (recurring) task.
 * @property {QueryBy} queryBy - Retrieves tasks from a query.
 */

/**
 * Hook to get a Task Data Transfer Object (DTO).
 * 
 * @returns {TaskDTO}
 */
export const useTaskDTO = () => {

    /**
     * @type {GetTask}
     */
    const get = async (docId) => {
        throw new Error('Not implemented yet');
    };
    
    /**
     * @type {CreateTask}
     */
    const create = async (task) => {
        await db.collection('Tasks').add(task);
    };

    /**
     * @type {UpdateTask}
     */
    const update = async (docId, updates) => {
        await db.collection('Tasks').doc(docId).update(updates);
    };

    /**
     * @type {SaveTask}
     */
    const save = async (task) => {
        /* Clone to avoid mutating original */
        const taskToSave = { ...task };

        /* Serialize rrule if present */
        if (taskToSave.rrule) {
            taskToSave.rrule = taskToSave.rrule.toString();
        } else {
            taskToSave.rrule = null;
        }

        if (taskToSave.docid) {
            await update(taskToSave.docid, taskToSave);
        } else {
            await create(taskToSave);
        }
    };

    /**
     * @type {RemoveTask}
     */
    const remove = async (docId, authId) => {
        if (!docId) return;

        // Zacht verwijderen (ADR-008): alle verwijderingen van afspraken lopen
        // hierlangs — de agenda, het rooster, subtaken en de teamplanning — dus
        // hard verwijderen maakte ze overal in één klap onherstelbaar.
        // DeletedAt via serverTimestamp, niet de `timestamp` uit
        // firebase/config: die staat vast op het moment van pagina laden.
        await db.collection('Tasks').doc(docId).update({
            Deleted: true,
            DeletedAt: firebase.firestore.FieldValue.serverTimestamp(),
            DeletedBy: authId || '',
        });
    };

    /**
     * @type {NewTaskDoc}
     */
    const newDoc = ({
        startDate,
        endDate,
        userId,
        companyId,
        AdressID = null,
        PersonaID = null,
        MeasureMomentID = null, 
        ActivityFormID = null,
        TeamMemberID = null,
        Type = null,
        }) => {
        return {
            ID: uuid(),
            AdressID,
            PersonaID,
            MeasureMomentID,
            ActivityFormID,
            TeamMemberID,
            Type,
            Description: '',
            Title: '',
            Tags: [],
            CompagnyID: companyId,
            Compagny: companyId,
            Position: 0,
            Timestamp: startDate ? firebase.firestore.Timestamp.fromDate(startDate) : null,
            TimestampEnd: endDate ? firebase.firestore.Timestamp.fromDate(endDate) : null,
            User: userId,
            Parent: null,
            rrule: null,
            exdate: [],
            exrule: null,
        };
    };

    /**
     * @type {DeleteRecurringTaskItem}
     */
    const deleteRecurringItem = async ({ task, deleteType, date }) => {
        
        if (deleteType === 'all') {

            /* Delete the entire recurring series */
            await remove(task.docid);
            
        } else if (deleteType === 'this') {

            /* Add selected date to exception dates */
            const exdate = task.exdate || [];

            const rrule = RRule.fromString(task.rrule);
            
            /* Black magic setting rrule exceptions to the dtstart/event time. */
            const dateToExclude = new Date(date);
            const year = dateToExclude.getUTCFullYear();
            const month = String(dateToExclude.getUTCMonth() + 1).padStart(2, '0');
            const day = String(dateToExclude.getUTCDate()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;
            
            const timeStr = rrule.options.dtstart.toISOString().split('T')[1];
            const isoDateExceptionString = `${dateStr}T${timeStr}`;

            /* Check if date already excluded */
            if (!exdate.includes(isoDateExceptionString)) {
                await update(task.docid, {
                    exdate: [...exdate, isoDateExceptionString]
                });
            }
        
        } else if (deleteType === 'following') {

            /* Modify rrule to end before clicked date */
            try {
                const rule = RRule.fromString(task.rrule);
                const options = rule.origOptions;
                
                /* Remove count and set until date */
                delete options.count;

                const untilDate = new Date(date);
                untilDate.setDate(untilDate.getDate() - 1);
                options.until = untilDate;
                
                const newRule = new RRule(options);

                await update(task.docid, { 
                    rrule: newRule.toString() 
                });
            } catch (error) {
                console.error('Error updating rrule:', error);
            }
        }
    };

    /**
     * @type {QueryBy}
     */
    const queryBy = ({ 
        query,
        dateStart,
        dateEnd,
        onUpdate, 
        alwaysIncludeRecurring = true 
    }) => {

        let baseQuery = db
            .collection('Tasks')
            .where('CompagnyID', '==', client);

        const tasks = query(baseQuery
            .where('Timestamp', '>=', firebase.firestore.Timestamp.fromDate(dateStart))
            .where('Timestamp', '<=', firebase.firestore.Timestamp.fromDate(dateEnd))
        );

        /* Query tasks with recurrence rules (always fetch) */
        const recurringTasks = query(
            baseQuery.where('rrule', '!=', null)
        );

        /* Subscribe to range query changes */
        const unsubscribe = tasks.onSnapshot((snapshot) => {
            const allItems = [];
            snapshot.forEach((doc) => {
                const task = { ...doc.data(), docid: doc.id };
                // Verwijderen is zacht (ADR-008), maar deze lees gaat langs de
                // hooks heen en dus langs de centrale filter van
                // useFirestoreQuery — die zit in de hooks, niet in de database.
                // Zonder dit blijft een verwijderde afspraak in de agenda staan
                // en verwijdert het team hem eindeloos opnieuw. Clientside en
                // geen `.where('Deleted','==',false)`: de meeste afspraken
                // dragen het veld helemaal niet en zouden dan wegvallen.
                // AI-CONTEXT: See docs/pitfalls.md#database-firestore-orderby-excludes-missing-field
                if (notDeleted(task)) allItems.push(task);
            });

            /* Fetch recurring tasks and merge */
            if (alwaysIncludeRecurring) {
                recurringTasks.get().then((recurringSnapshot) => {
                    const recurringArray = [];
                    recurringSnapshot.forEach((doc) => {
                        const task = { ...doc.data(), docid: doc.id };
                        // Tweede query, eigen filter: de herhalende afspraken
                        // komen hier los binnen, dus filteren op alleen de
                        // reeks hierboven laat precies deze groep staan.
                        if (!notDeleted(task)) return;

                        /* Filter out duplicates */
                        if (!allItems.find((t) => t.docid === doc.id)) {
                            recurringArray.push(task);
                        }
                    });
                    onUpdate([...allItems, ...recurringArray]);
                }).catch((error) => {
                    // Zonder deze catch verdwijnt een mislukte rrule-query stil en
                    // komt onUpdate nooit: de agenda blijft dan leeg.
                    console.error(describeQueryError('Tasks', [['rrule', '!=', null]], error), error);
                    onUpdate(allItems);
                });
            } else {
                onUpdate(allItems);
            }
        }, (error) => {
            // AI-CONTEXT: See docs/pitfalls.md#database-firestore-silent-listener-failure
            console.error(describeQueryError('Tasks', [
                ['CompagnyID', '==', client],
                ['Timestamp', '>=', dateStart],
                ['Timestamp', '<=', dateEnd],
            ], error), error);
        });

        return unsubscribe;
    };

    return {
        get,
        create,
        update,
        save,
        remove,
        newDoc,
        deleteRecurringItem,
        queryBy,
    };
};
